import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import {
  Box,
  Checkbox,
  IconButton,
  LinearProgress,
  TextField,
  Typography
} from '@mui/material';
import { emptyCells } from 'common/constants';
import {
  BudgetValueState,
  IBudget,
  IBudgetPeriod,
  IExpense
} from 'common/entities';
import { Currency, IMonth, ITableColumn } from 'common/types';
import SortableTable from 'components/Shared/SortableTable';
import EditableNumberCell from 'components/table/EditableNumberCell';
import { useAppDispatch, useAppSelector } from 'core/hooks/hooks';
import { getBudgetMonthSpent } from 'core/services/expense.service';
import { periodSelector } from 'core/store/selectors/period.selector';
import { useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { theme } from 'theme';
import { BudgetMonthContext } from './BudgetMonthContextProvider';
import BudgetMonthPanel from './BudgetMonthPanel';
import PrevMonthResidue from './PrevMonthResidue';
import { setMonthBudget } from 'core/store/actions/budgetMonth.actions';
import { useCurrency } from 'core/hooks/useCurrency';

interface IMonthBudgetRecord {
  id: string;
  name: string;
  progress?: number;
  budgetSum?: number;
  spent?: number;
  balance?: number;
  selected: boolean;
}

export interface BudgetListItemProps {
  budget: IBudget;
  expenses: IExpense[];
  monthDate: Date;
}

export interface BudgetMonthBudgetsPanelProps {
  budgets: IBudget[];
  expenses: IExpense[];
  onCreateBudget: () => void;
}

const createColumns = (
  isEditing: boolean
): ITableColumn<IMonthBudgetRecord>[] =>
  [
    ...(isEditing
      ? [
          {
            id: 'selected',
            numeric: false,
            label: '',
            editable: false
          }
        ]
      : []),
    {
      id: 'name',
      numeric: false,
      label: 'Budget',
      editable: false
    },
    ...(!isEditing
      ? [
          {
            id: 'progress',
            numeric: false,
            label: ''
          },
          {
            id: 'balance',
            numeric: true,
            label: 'Spent/Balance',
            editable: false
          }
        ]
      : ([] as ITableColumn<IMonthBudgetRecord>[])),
    {
      id: 'budgetSum',
      numeric: true,
      label: 'Month budget',
      align: 'right',
      editable: true
    }
  ] as ITableColumn<IMonthBudgetRecord>[];

const createBudgetRow = (
  budget: IBudget | undefined,
  expenses: IExpense[],
  monthDate: IMonth,
  selectedBudgets: Record<string, BudgetValueState>,
  currency: Currency
): IMonthBudgetRecord => {
  const spent = getBudgetMonthSpent(budget?.id, expenses, monthDate, currency);
  const monthBudget = budget?.id
    ? selectedBudgets[budget.id]?.value ?? budget.defaultBudget ?? 0
    : 0;
  return {
    id: budget?.id ?? '0',
    name: budget?.name ?? emptyCells.budget,
    budgetSum: monthBudget,
    progress: monthBudget ? (spent / monthBudget) * 100 : undefined,
    spent,
    balance: monthBudget - spent,
    selected: budget?.id ? !!selectedBudgets[budget.id]?.selected : false
  };
};

const createRows = (
  budgets: IBudget[],
  expenses: IExpense[],
  budgetMonth: IMonth,
  includeDefault: boolean,
  selectedBudgets: Record<string, BudgetValueState>,
  currency: Currency
) => {
  const budgetRows = budgets
    .filter(b => !b.accumulation)
    .map(b =>
      createBudgetRow(b, expenses, budgetMonth, selectedBudgets, currency)
    );
  return includeDefault
    ? [createBudgetRow(undefined, expenses, budgetMonth, {}, currency)].concat(
        budgetRows
      )
    : budgetRows;
};

const AddItemButton = ({ onClick }: { onClick: () => void }) => (
  <IconButton onClick={onClick}>
    <AddCircleOutlinedIcon />
  </IconButton>
);

const BudgetMonthBudgetsPanel = ({
  budgets,
  expenses,
  onCreateBudget
}: BudgetMonthBudgetsPanelProps) => {
  const dispatch = useAppDispatch();
  const {
    isEditing,
    selectedBudgets,
    budgetValues,
    onUpdateBudgetValue,
    onBudgetToggleSelected
  } = useContext(BudgetMonthContext);
  const budgetMonth = useAppSelector(periodSelector);
  const { sumWithCurrency, currency } = useCurrency();

  const budgetColumns = useMemo(() => createColumns(isEditing), [isEditing]);

  const monthBudgets = useMemo(
    () =>
      budgets.filter(
        b =>
          !b.accumulation &&
          (isEditing ||
            (Object.keys(selectedBudgets).includes(b.id) &&
              selectedBudgets[b.id]?.selected))
      ),
    [budgets, budgetMonth, isEditing, selectedBudgets]
  );

  const budgetRows = useMemo(
    () =>
      createRows(
        monthBudgets,
        expenses,
        budgetMonth,
        !isEditing,
        selectedBudgets,
        currency
      ),
    [monthBudgets, budgetMonth, isEditing, selectedBudgets, expenses]
  );

  const renderBudgetSumItem = (value: any) => <>{sumWithCurrency(value)}</>;

  const createBudgetMonth = useCallback(
    (budgetSum: number | undefined): IBudgetPeriod => ({
      month: budgetMonth.month,
      year: budgetMonth.year,
      budget: budgetSum
    }),
    [budgetMonth]
  );

  const handleUpdateBudgetMonthSum = useCallback(
    (budgetId: string, budgetMonth: IBudgetPeriod) => {
      dispatch(
        setMonthBudget({
          budgetId,
          budget: budgetMonth.budget,
          month: budgetMonth.month,
          year: budgetMonth.year
        })
      );
    },
    [dispatch]
  );

  const budgetsCustomCellBuilder = useMemo(
    () =>
      (
        column: keyof IMonthBudgetRecord,
        row: IMonthBudgetRecord
      ): JSX.Element => {
        switch (column) {
          case 'selected':
            return (
              <Checkbox
                checked={!!budgetValues[row.id]?.selected}
                onChange={() => onBudgetToggleSelected(row.id)}
              />
            );
          case 'name':
            return row.id === '0' ? (
              <Typography
                component={'span'}
                sx={{ color: '#555', fontSize: '12px' }}
              >
                {row.name}
              </Typography>
            ) : (
              <Link
                to={{
                  pathname: `/budget/${row.id}`,
                  state: {
                    goBackTitle: 'Back to Month Budgets'
                  }
                }}
                style={{ color: theme.palette.common.black }}
              >
                {row.name}
              </Link>
            );
          case 'progress':
            return row.progress !== undefined ? (
              <Box
                sx={{
                  ml: '10px',
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center'
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={row.progress}
                  sx={{ height: '1rem', width: '150px' }}
                />
              </Box>
            ) : (
              <></>
            );
          case 'budgetSum':
            return row.id === '0' ? (
              <Typography component={'span'} sx={{ marginRight: '25px' }}>
                -
              </Typography>
            ) : isEditing ? (
              <TextField
                sx={{ width: '80px' }}
                type="number"
                variant="standard"
                value={budgetValues[row.id]?.value ?? ''}
                onChange={e =>
                  onUpdateBudgetValue(
                    row.id,
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                disabled={!budgetValues[row.id]?.selected}
              />
            ) : (
              <>
                <EditableNumberCell
                  initialValue={Number(row.budgetSum) ?? 0}
                  onSaveValue={(value: number) =>
                    handleUpdateBudgetMonthSum(row.id, createBudgetMonth(value))
                  }
                  renderValue={renderBudgetSumItem}
                />
              </>
            );
          case 'spent':
            return <></>;
          case 'balance':
            return row.id === '0' ? (
              <>-</>
            ) : (
              <>
                {`${sumWithCurrency(row['spent'] ?? 0)} / ${sumWithCurrency(
                  row['balance'] ?? 0
                )}`}
              </>
            );
          default:
            return <>{row[column]}</>;
        }
      },
    [handleUpdateBudgetMonthSum, createBudgetMonth, isEditing, budgetValues]
  );

  return (
    <BudgetMonthPanel
      title="Budgets"
      actions={<AddItemButton onClick={onCreateBudget} />}
    >
      <PrevMonthResidue />
      <SortableTable
        columns={budgetColumns}
        rows={budgetRows}
        indexBy="id"
        defaultOrderBy={'name'}
        customCellBuilder={budgetsCustomCellBuilder}
        styles={{
          cell: { borderBottom: 'none', px: 0, py: '.3rem' },
          headCell: { borderBottom: 'none', px: 0, py: '.3rem' }
        }}
        getRowExpandedContent={(row: IMonthBudgetRecord) =>
          !isEditing ? (
            <Box
              sx={{
                width: '100%',
                display: { xs: 'block', md: 'none' },
                mb: 2
              }}
            >
              {row.progress !== undefined ? (
                <LinearProgress
                  variant="determinate"
                  value={row.progress}
                  sx={{ height: '1rem' }}
                />
              ) : null}
            </Box>
          ) : (
            <></>
          )
        }
      />
    </BudgetMonthPanel>
  );
};

export default BudgetMonthBudgetsPanel;
